import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Users, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AllUsersTab({ allUsers, searchTerm, onSearchChange, onRefresh }) {
  const filteredUsers = allUsers.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteUser = async (userId, userName, email) => {
    if (!window.confirm(`PERMANENTLY DELETE user "${userName}" (${email})?\n\nThis will:\n- Remove the user account\n- Delete all progress, badges, exams\n- Free up their activation code\n\nThis cannot be undone!`)) return;
    try {
      const res = await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`${email} permanently deleted${res.data.code_freed ? ` (code ${res.data.code_freed} freed)` : ''}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetDevice = async (userId, userName) => {
    try {
      await axios.post(`${API}/admin/reset-device/${userId}`);
      toast.success(`Device reset for ${userName}`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to reset device');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-500" /> All Users ({filteredUsers.length})
        </CardTitle>
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name or email..." value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} className="pl-10" data-testid="user-search" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {filteredUsers.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white text-sm font-bold">
                  {u.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-medium text-sm">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                  {u.device_id && <p className="text-[10px] text-muted-foreground/60 font-mono truncate max-w-[180px]" title={u.device_id}>Device: {u.device_id.slice(0, 16)}...</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={u.status === 'approved' ? 'default' : u.status === 'pending' ? 'secondary' : 'destructive'}>{u.status || 'approved'}</Badge>
                <span className="text-xs text-muted-foreground">{u.current_level}</span>
                {u.device_id && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
                    onClick={() => handleResetDevice(u.user_id, u.name)}
                    data-testid={`reset-device-${u.user_id}`}>
                    Reset Device
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => handleDeleteUser(u.user_id, u.name, u.email)}
                  data-testid={`delete-user-${u.user_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
